import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { conversationsAPI, feedbackAPI, capsulesAPI, inboxAPI, formatApiError } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocketToken';
import useCapsuleRoutingCatalogs from '@/hooks/useCapsuleRoutingCatalogs';
import RoutingMetadataForm from '@/components/RoutingMetadataForm';
import ActividadAgentica from '@/components/ActividadAgentica';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Eye, Hand, ArrowLeft, Send, User, Bot, Shield, RefreshCw,
  Star, Flag, BookOpen, Pencil, AlertTriangle, Zap, Trash2, Loader2, CheckCircle2, GitMerge,
} from 'lucide-react';
import { toast } from 'sonner';
import ConversationListItem, { getConversationDisplay } from '@/components/ConversationListItem';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import EmailMessageContext, { formatDateTime } from '@/components/EmailMessageContext';
import EmailMessageBody from '@/components/EmailMessageBody';
import { MessageAttachments } from '@/components/MessageAttachments';
import { fechaYHora, soloHora, soloFecha } from '../lib/fechas';

// Format timestamp like WhatsApp: "HH:MM"
function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return soloHora(d, '');
}

function formatDateSeparator(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (today - msgDay) / (1000 * 60 * 60 * 24);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return soloFecha(d, '');
}

function isDifferentDay(ts1, ts2) {
  if (!ts1 || !ts2) return true;
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);
  return d1.toDateString() !== d2.toDateString();
}

export default function MonitorPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
  const [supervisorMsg, setSupervisorMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [filters, setFilters] = useState({
    channel: '',
    no_ticket: false,
    human_reviewed: undefined,
    pending_outbound: false,
  });
  const messagesEndRef = useRef(null);
  const tenantId = user?.tenant_id;
  const [queryCapsuleIds, setQueryCapsuleIds] = useState([]);
  const [mergeSuggestionMap, setMergeSuggestionMap] = useState({});
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState('');
  const [mergeReason, setMergeReason] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePhrase, setPurgePhrase] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const PURGE_PHRASE = 'BORRAR CONVERSACIONES';
  const isAdmin = user?.role === 'admin';

  const hasTriageFilters = Boolean(
    filters.channel || filters.no_ticket || filters.human_reviewed === false || filters.pending_outbound
  );

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await capsulesAPI.list(tenantId);
        const allIds = (res.data || [])
          .filter((c) => c?.status !== 'archived')
          .map((c) => c.capsule_id)
          .filter(Boolean);
        const member = (user?.capsule_ids || []).filter(Boolean);
        let ids;
        if (member.length) ids = allIds.filter((id) => member.includes(id));
        else if (user?.role === 'admin' || user?.role === 'supervisor') ids = allIds;
        else ids = [];
        if (!cancelled) setQueryCapsuleIds(ids);
      } catch {
        if (!cancelled) setQueryCapsuleIds((user?.capsule_ids || []).filter(Boolean));
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, user?.capsule_ids, user?.role]);

  // Feedback dialog states
  const [rateOpen, setRateOpen] = useState(false);
  const [rateMsg, setRateMsg] = useState(null);
  const [rateValue, setRateValue] = useState(0);
  const [rateComment, setRateComment] = useState('');

  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctMsg, setCorrectMsg] = useState(null);
  const [correctedText, setCorrectedText] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');

  const [learnOpen, setLearnOpen] = useState(false);
  const [learnContent, setLearnContent] = useState('');
  const [learnCategory, setLearnCategory] = useState('general');

  const [cmMeta, setCmMeta] = useState({
    intent: '',
    category: '',
    label: '',
    priority: 'medium',
    requesting_department: '',
  });
  const [cmSaving, setCmSaving] = useState(false);

  const convCapsuleId = selectedConv?.capsule_id || queryCapsuleIds[0] || '';
  const routingCatalogs = useCapsuleRoutingCatalogs(tenantId, convCapsuleId);
  const selectedConvRef = useRef(null);
  selectedConvRef.current = selectedConv;

  const loadFeedbackStats = useCallback(async () => {
    try {
      const capsuleId = queryCapsuleIds[0];
      if (!capsuleId) return;
      const res = await feedbackAPI.stats(capsuleId);
      setFeedbackStats(res.data);
    } catch (err) { console.error(err); }
  }, [queryCapsuleIds]);

  const prefetchMergeSuggestions = useCallback(async (items) => {
    if (!tenantId || !queryCapsuleIds.length) return;
    const emailItems = (items || []).filter((c) => (c.channel || '').toLowerCase() === 'email').slice(0, 25);
    if (!emailItems.length) return;
    const results = await Promise.allSettled(
      emailItems.map(async (c) => {
        const capId = c.capsule_id || queryCapsuleIds[0];
        const res = await conversationsAPI.mergeSuggestions(tenantId, capId, c.conversation_id);
        return [c.conversation_id, res.data?.suggestions || []];
      })
    );
    const map = {};
    for (const r of results) {
      if (r.status === 'fulfilled') map[r.value[0]] = r.value[1];
    }
    setMergeSuggestionMap(map);
  }, [tenantId, queryCapsuleIds]);

  const loadConversations = useCallback(async () => {
    if (!tenantId || !queryCapsuleIds.length) {
      setConversations([]);
      return;
    }
    setListLoading(true);
    try {
      let merged = [];
      if (hasTriageFilters) {
        const params = {};
        queryCapsuleIds.forEach((id) => {
          if (!params.capsule_id) params.capsule_id = [];
          params.capsule_id.push(id);
        });
        if (filters.channel) params.channel = filters.channel;
        if (filters.no_ticket) params.no_ticket = true;
        if (filters.human_reviewed === false) params.human_reviewed = false;
        if (filters.pending_outbound) params.pending_outbound = true;
        const res = await inboxAPI.list(tenantId, params);
        merged = res.data || [];
      } else {
        const results = await Promise.allSettled(queryCapsuleIds.map((cid) => conversationsAPI.list(tenantId, cid)));
        for (const r of results) {
          if (r.status === 'fulfilled') merged.push(...(r.value.data || []));
        }
        merged = Object.values(merged.reduce((acc, c) => {
          acc[c.conversation_id] = c;
          return acc;
        }, {}));
        merged.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      }
      setConversations(merged);
      prefetchMergeSuggestions(merged);
      const current = selectedConvRef.current;
      if (current) {
        const selectedCapsuleId = current.capsule_id || queryCapsuleIds[0];
        const fresh = await conversationsAPI.get(tenantId, selectedCapsuleId, current.conversation_id);
        setSelectedConv(fresh.data);
      }
    } catch (err) {
      console.error(err);
      toast.error(formatApiError(err));
    } finally {
      setListLoading(false);
    }
  }, [tenantId, queryCapsuleIds, hasTriageFilters, filters, prefetchMergeSuggestions]);

  useEffect(() => {
    if (tenantId) {
      loadFeedbackStats();
    }
  }, [tenantId, loadFeedbackStats]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const state = location.state;
    if (!state?.conversationId || !tenantId || !queryCapsuleIds.length) return;
    let cancelled = false;
    (async () => {
      try {
        const capsuleId = state.capsuleId || queryCapsuleIds[0];
        const res = await conversationsAPI.get(tenantId, capsuleId, state.conversationId);
        if (!cancelled) setSelectedConv(res.data);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, [location.state, tenantId, queryCapsuleIds]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConv]);

  useEffect(() => {
    if (!selectedConv) return;
    setCmMeta({
      intent: selectedConv.intent || '',
      category: selectedConv.category || '',
      label: selectedConv.label || '',
      priority: selectedConv.priority || 'medium',
      requesting_department: selectedConv.requesting_department || '',
    });
  }, [selectedConv?.conversation_id, selectedConv?.intent, selectedConv?.category, selectedConv?.label, selectedConv?.priority, selectedConv?.requesting_department]);

  const handleSaveConvRoutingMetadata = async () => {
    if (!selectedConv) return;
    const convCapsuleIdLocal = selectedConv?.capsule_id || queryCapsuleIds[0];
    if (!convCapsuleIdLocal) {
      toast.error('No hay cápsula resuelta para esta conversación');
      return;
    }
    setCmSaving(true);
    try {
      await conversationsAPI.patchRoutingMetadata(tenantId, convCapsuleIdLocal, selectedConv.conversation_id, {
        intent: cmMeta.intent,
        category: cmMeta.category,
        label: cmMeta.label,
        priority: cmMeta.priority,
        requesting_department: cmMeta.requesting_department || null,
        sync_ticket: true,
      });
      toast.success('Metadatos de conversacion actualizados');
      const fresh = await conversationsAPI.get(tenantId, convCapsuleIdLocal, selectedConv.conversation_id);
      setSelectedConv(fresh.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCmSaving(false);
    }
  };

  const handleWsEvent = useCallback((data) => {
    if (data.type === 'new_message') {
      setSelectedConv(prev => {
        if (!prev || prev.conversation_id !== data.conversation_id) return prev;
        const msgs = prev.messages || [];
        if (msgs.find(m => m.message_id === data.message.message_id)) return prev;
        return { ...prev, messages: [...msgs, data.message] };
      });
      toast.info('Nuevo mensaje', { description: data.message?.content?.slice(0, 60) });
    } else if (data.type === 'hitl_change') {
      toast.info(data.action === 'takeover' ? 'Takeover activado' : 'Handback completado');
      loadConversations();
    }
  }, [loadConversations]);

  useWebSocket(tenantId ? '/ws/monitor' : null, {
    enabled: !!tenantId,
    onMessage: handleWsEvent,
    onOpen: () => setWsConnected(true),
    onClose: () => setWsConnected(false),
  });

  useEffect(() => {
    if (!tenantId) return undefined;
    const interval = setInterval(() => loadConversations(), 15000);
    return () => clearInterval(interval);
  }, [tenantId, loadConversations]);

  const markReviewed = async (conv) => {
    try {
      await inboxAPI.patchTriage(tenantId, conv.capsule_id, conv.conversation_id, { human_reviewed: true });
      toast.success('Marcado como revisado');
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const selectConversation = async (conv) => {
    try {
      const convCapsuleId = conv?.capsule_id || queryCapsuleIds[0];
      const res = await conversationsAPI.get(tenantId, convCapsuleId, conv.conversation_id);
      setSelectedConv(res.data);
    } catch (err) { console.error(err); }
  };

  const handleTakeover = async () => {
    if (!selectedConv) return;
    try {
      const convCapsuleId = selectedConv?.capsule_id || queryCapsuleIds[0];
      await conversationsAPI.takeover(tenantId, convCapsuleId, selectedConv.conversation_id, 'Supervisor takeover');
      loadConversations();
      toast.success('Takeover activado');
    } catch (err) { console.error(err); }
  };

  const handleHandback = async () => {
    if (!selectedConv) return;
    try {
      const convCapsuleId = selectedConv?.capsule_id || queryCapsuleIds[0];
      await conversationsAPI.handback(tenantId, convCapsuleId, selectedConv.conversation_id);
      loadConversations();
      toast.success('Handback completado');
    } catch (err) { console.error(err); }
  };

  const handleSendSupervisorMessage = async (e) => {
    e.preventDefault();
    if (!supervisorMsg.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      const convCapsuleId = selectedConv?.capsule_id || queryCapsuleIds[0];
      await conversationsAPI.sendMessage(tenantId, convCapsuleId, selectedConv.conversation_id, {
        content: supervisorMsg,
        sender_type: 'human',
        sender_id: String(user?._id || ''),
        sender_name: user?.full_name,
      });
      setSupervisorMsg('');
      const fresh = await conversationsAPI.get(tenantId, convCapsuleId, selectedConv.conversation_id);
      setSelectedConv(fresh.data);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  // Feedback handlers
  const handleRate = async () => {
    if (!rateMsg || rateValue < 1) return;
    try {
      await feedbackAPI.rate({
        conversation_id: selectedConv.conversation_id,
        message_id: rateMsg.message_id,
        rating: rateValue,
        comment: rateComment || null,
      });
      setRateOpen(false);
      setRateValue(0);
      setRateComment('');
      toast.success(`Rating: ${rateValue}/5 guardado`);
      loadFeedbackStats();
    } catch (err) { console.error(err); }
  };

  const handleCorrection = async () => {
    if (!correctMsg || !correctedText.trim()) return;
    try {
      await feedbackAPI.correct({
        conversation_id: selectedConv.conversation_id,
        message_id: correctMsg.message_id,
        original_response: correctMsg.content,
        corrected_response: correctedText,
        correction_reason: correctionReason,
      });
      setCorrectOpen(false);
      setCorrectedText('');
      setCorrectionReason('');
      toast.success('Correccion guardada');
      loadFeedbackStats();
    } catch (err) { console.error(err); }
  };

  const handleFlag = async (msg) => {
    try {
      await feedbackAPI.flag({
        conversation_id: selectedConv.conversation_id,
        message_id: msg.message_id,
        reason: 'Flagged by supervisor',
      });
      toast.warning('Mensaje flaggeado');
    } catch (err) { console.error(err); }
  };

  const handleInjectLearning = async () => {
    if (!learnContent.trim()) return;
    const agentId = selectedConv?.current_agent?.agent_id || 'default-agent';
    const capsuleId = selectedConv?.capsule_id || queryCapsuleIds[0];
    try {
      await feedbackAPI.learn({
        agent_id: agentId,
        capsule_id: capsuleId,
        content: learnContent,
        category: learnCategory,
        should_add_to_memory: true,
      });
      setLearnOpen(false);
      setLearnContent('');
      toast.success('Aprendizaje inyectado en MEMORY.md');
      loadFeedbackStats();
    } catch (err) { console.error(err); }
  };

  const handleDeleteConversation = async (conv) => {
    if (!conv?.conversation_id) return;
    setConfirmState({
      open: true,
      title: 'Eliminar conversacion',
      description: `Se eliminara la conversacion ${conv.conversation_id.slice(0, 8)}.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          const convCapsuleId = conv?.capsule_id || queryCapsuleIds[0];
          await conversationsAPI.delete(tenantId, convCapsuleId, conv.conversation_id);
          toast.success('Conversacion eliminada');
          if (selectedConv?.conversation_id === conv.conversation_id) setSelectedConv(null);
          await loadConversations();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'No se pudo eliminar la conversacion');
        }
      },
    });
  };

  const handleApproveOutbound = async () => {
    if (!selectedConv) return;
    const convCapsuleId = selectedConv?.capsule_id || queryCapsuleIds[0];
    try {
      await conversationsAPI.approveOutbound(tenantId, convCapsuleId, selectedConv.conversation_id);
      toast.success('Mail enviado');
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleRejectOutbound = async () => {
    if (!selectedConv) return;
    const convCapsuleId = selectedConv?.capsule_id || queryCapsuleIds[0];
    try {
      await conversationsAPI.rejectOutbound(tenantId, convCapsuleId, selectedConv.conversation_id, {});
      toast.success('Envio descartado');
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const mergeCandidates = conversations.filter(
    (c) => c.conversation_id !== selectedConv?.conversation_id
      && (c.channel || '').toLowerCase() === 'email'
      && c.status !== 'merged'
  );

  const selectedMergeSuggestions = selectedConv
    ? (mergeSuggestionMap[selectedConv.conversation_id] || [])
    : [];

  const handleMergeConversations = () => {
    if (!selectedConv || !mergeSourceId) return;
    const source = conversations.find((c) => c.conversation_id === mergeSourceId);
    setConfirmState({
      open: true,
      title: 'Fusionar conversaciones',
      description: `Se uniran los mensajes de "${getConversationDisplay(source || {}).primary}" en "${getConversationDisplay(selectedConv).primary}". La conversacion origen quedara archivada como fusionada.`,
      confirmText: 'Fusionar',
      onConfirm: async () => {
        setMergeLoading(true);
        try {
          const capId = selectedConv.capsule_id || queryCapsuleIds[0];
          const res = await conversationsAPI.merge(tenantId, capId, selectedConv.conversation_id, {
            source_conversation_id: mergeSourceId,
            reason: mergeReason || undefined,
          });
          toast.success('Conversaciones fusionadas');
          setMergeOpen(false);
          setMergeSourceId('');
          setMergeReason('');
          setSelectedConv(res.data?.conversation || selectedConv);
          loadConversations();
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setMergeLoading(false);
        }
      },
    });
  };

  const isHumanActive = selectedConv?.hitl?.is_human_active;

  const handlePurgeAllConversations = async () => {
    if (!tenantId || purgePhrase.trim() !== PURGE_PHRASE) return;
    setPurgeLoading(true);
    try {
      const res = await conversationsAPI.purgeAll(tenantId, { confirmation: purgePhrase.trim() });
      toast.success(`Eliminadas ${res.data?.conversations_deleted ?? 0} conversaciones`);
      setPurgeOpen(false);
      setPurgePhrase('');
      setSelectedConv(null);
      await loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurgeLoading(false);
    }
  };

  return (
    <div className="flex h-full" data-testid="monitor-page">
      {/* Conversation list */}
      <div className="w-80 border-r border-brand-border bg-white flex flex-col">
        <div className="h-14 flex items-center justify-between px-4 border-b border-brand-border">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Monitor HITL</span>
            <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-brand-success' : 'bg-zinc-300'}`} title={wsConnected ? 'WS conectado' : 'WS desconectado'} />
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setPurgeOpen(true)}
                data-testid="purge-all-conversations"
              >
                Vaciar conversaciones
              </Button>
            )}
            <Button variant="ghost" size="icon" data-testid="refresh-monitor" onClick={loadConversations} disabled={listLoading} className="h-8 w-8">
              <RefreshCw className={`w-3 h-3 ${listLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="px-3 py-2 border-b border-brand-border flex flex-wrap gap-2" data-testid="monitor-inbox-filters">
          <select
            className="text-xs border border-brand-border rounded-sm px-2 py-1"
            value={filters.channel}
            onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))}
          >
            <option value="">Todos los canales</option>
            <option value="email">Email</option>
            <option value="teams">Teams</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="webchat">WebChat</option>
          </select>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={filters.no_ticket}
              onChange={(e) => setFilters((f) => ({ ...f, no_ticket: e.target.checked }))}
            />
            Sin ticket
          </label>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={filters.human_reviewed === false}
              onChange={(e) => setFilters((f) => ({
                ...f,
                human_reviewed: e.target.checked ? false : undefined,
              }))}
            />
            Sin revisar
          </label>
          <label className="text-xs flex items-center gap-1">
            <input
              type="checkbox"
              checked={filters.pending_outbound}
              onChange={(e) => setFilters((f) => ({ ...f, pending_outbound: e.target.checked }))}
            />
            Mail pendiente
          </label>
        </div>

        {/* Feedback stats */}
        {feedbackStats && (
          <div className="px-4 py-2 border-b border-brand-border bg-brand-surface">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500">Rating promedio</span>
              <span className="font-mono font-bold text-brand-primary">{feedbackStats.average_rating}/5</span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-400">
              <span>{feedbackStats.total_ratings} ratings</span>
              <span>{feedbackStats.total_corrections} correcciones</span>
              <span>{feedbackStats.total_flags} flags</span>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {conversations.length === 0 ? (
            <p className="p-4 text-xs text-zinc-400">
              {hasTriageFilters ? 'Sin conversaciones con estos filtros' : 'Sin conversaciones activas'}
            </p>
          ) : (
            conversations.map(conv => (
              <ConversationListItem
                key={conv.conversation_id}
                conv={conv}
                selected={selectedConv?.conversation_id === conv.conversation_id}
                onClick={() => selectConversation(conv)}
                formatTime={formatTime}
                hasMergeSuggestion={Boolean((mergeSuggestionMap[conv.conversation_id] || []).length)}
                trailing={
                  <div className="flex items-center gap-1">
                    {!conv.triage?.human_reviewed && (
                      <button
                        type="button"
                        className="text-zinc-300 hover:text-brand-success"
                        onClick={(e) => { e.stopPropagation(); markReviewed(conv); }}
                        title="Marcar revisado"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="text-zinc-300 hover:text-red-600"
                      onClick={(e) => { e.stopPropagation(); handleDeleteConversation(conv); }}
                      title="Eliminar conversacion"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                }
              />
            ))
          )}
        </div>
      </div>

      {/* Conversation detail */}
      <div className="flex-1 flex flex-col">
        {!selectedConv ? (
          <div className="flex items-center justify-center h-full text-zinc-400">
            <div className="text-center">
              <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Selecciona una conversacion para monitorear</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="h-14 flex items-center justify-between px-6 border-b border-brand-border bg-white">
              <div className="flex items-center gap-3 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">
                    {getConversationDisplay(selectedConv).primary}
                  </p>
                  <p className="text-[10px] text-zinc-500 truncate">
                    {getConversationDisplay(selectedConv).secondary}
                    <span className="font-mono text-zinc-400 ml-2">{selectedConv.conversation_id.slice(0, 8)}</span>
                  </p>
                </div>
                <Badge variant="outline" className="rounded-sm text-xs shrink-0">{selectedConv.status}</Badge>
                {isHumanActive && <Badge className="bg-brand-danger text-white rounded-sm text-xs">Supervisor activo</Badge>}
                {selectedConv.current_agent?.agent_id && (
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-brand-primary" />
                    <span className="text-xs font-mono text-brand-primary">{selectedConv.current_agent.agent_id}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                {selectedConv.created_at && (
                  <span className="text-[10px] text-zinc-400">Creado: {fechaYHora(selectedConv.created_at)}</span>
                )}
                {/* Learning injection */}
                <Dialog open={learnOpen} onOpenChange={setLearnOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-sm text-xs h-8 px-3" data-testid="inject-learning-button">
                      <BookOpen className="w-3 h-3 mr-1" /> Inyectar
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-sm">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Inyectar Aprendizaje</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.1em]">Categoria</Label>
                        <select
                          data-testid="learn-category-select"
                          value={learnCategory}
                          onChange={e => setLearnCategory(e.target.value)}
                          className="w-full border border-brand-border rounded-sm p-2 text-sm"
                        >
                          <option value="oracle_forms">Oracle Forms</option>
                          <option value="oracle_reports">Oracle Reports</option>
                          <option value="base_datos">Base de Datos</option>
                          <option value="redes">Redes</option>
                          <option value="accesos">Accesos</option>
                          <option value="procedimiento">Procedimiento</option>
                          <option value="general">General</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.1em]">Contenido</Label>
                        <textarea
                          data-testid="learn-content-input"
                          value={learnContent}
                          onChange={e => setLearnContent(e.target.value)}
                          className="w-full border border-brand-border rounded-sm p-2 text-sm h-32 focus:ring-2 focus:ring-brand-primary focus:outline-none font-mono"
                          placeholder="Escribe el aprendizaje que el agente debe recordar..."
                        />
                      </div>
                      <Button data-testid="learn-submit-button" onClick={handleInjectLearning} className="w-full bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm">
                        Inyectar en MEMORY.md
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Email co-pilot pending approval */}
                {selectedConv?.pending_outbound?.status === 'pending_approval' && (
                  <>
                    <Button
                      onClick={handleApproveOutbound}
                      className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8 px-3"
                    >
                      Aprobar mail
                    </Button>
                    <Button
                      onClick={handleRejectOutbound}
                      variant="outline"
                      className="rounded-sm text-xs h-8 px-3"
                    >
                      Descartar mail
                    </Button>
                  </>
                )}

                {(user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'operator')
                  && (selectedConv?.channel || '').toLowerCase() === 'email' && (
                  <Button
                    variant="outline"
                    className="rounded-sm text-xs h-8 px-3"
                    onClick={() => { setMergeSourceId(''); setMergeOpen(true); }}
                  >
                    <GitMerge className="w-3 h-3 mr-1" /> Fusionar con...
                  </Button>
                )}

                {/* Takeover / Handback */}
                {!isHumanActive ? (
                  <Button
                    data-testid="takeover-conversation-button"
                    onClick={handleTakeover}
                    className="bg-brand-danger hover:bg-red-700 text-white rounded-sm text-xs h-8 px-3"
                  >
                    <Hand className="w-3 h-3 mr-1" /> Takeover
                  </Button>
                ) : (
                  <Button
                    data-testid="handback-conversation-button"
                    onClick={handleHandback}
                    variant="outline"
                    className="rounded-sm text-xs h-8 px-3 border-brand-success text-brand-success"
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" /> Handback
                  </Button>
                )}
                <Button
                  onClick={() => handleDeleteConversation(selectedConv)}
                  variant="outline"
                  className="rounded-sm text-xs h-8 px-3 border-red-500 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                </Button>
              </div>
            </div>

            {(user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'operator') && (
              <div className="px-6 py-3 border-b border-brand-border bg-zinc-50/90 space-y-2" data-testid="conv-routing-metadata">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Clasificacion (correccion manual)</p>
                <RoutingMetadataForm
                  value={cmMeta}
                  onChange={setCmMeta}
                  catalogs={routingCatalogs}
                  dense
                  show={{ intent: true, category: true, label: true, labels: false, priority: true, requesting_department: true }}
                />
                <Button type="button" size="sm" className="h-8 text-xs bg-brand-primary text-white rounded-sm" disabled={cmSaving || routingCatalogs.loading} onClick={handleSaveConvRoutingMetadata}>
                  {cmSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Guardar metadatos
                </Button>
              </div>
            )}

            {/*
              Antes de los mensajes: la última burbuja no dice si el agente está pensando la
              siguiente o si se murió a mitad de camino. Sin esto, las dos cosas se ven igual
              — una conversación que quedó callada.
            */}
            <div className="px-6 pt-3">
              <ActividadAgentica
                tenantId={tenantId}
                capsuleId={selectedConv.capsule_id || queryCapsuleIds[0]}
                conversationId={selectedConv.conversation_id}
              />
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-auto p-6 space-y-1 bg-brand-surface" data-testid="monitor-messages">
              {(selectedConv.messages || []).map((msg, idx) => {
                const prevMsg = idx > 0 ? (selectedConv.messages || [])[idx - 1] : null;
                const showDateSep = isDifferentDay(prevMsg?.timestamp, msg.timestamp);
                const isUser = msg.sender_type === 'user';
                const isHuman = msg.sender_type === 'human';
                const isSystem = msg.sender_type === 'system';

                return (
                  <div key={msg.message_id}>
                    {showDateSep && msg.timestamp && (
                      <div className="flex items-center justify-center my-3">
                        <span className="text-[10px] text-zinc-400 bg-zinc-100 px-3 py-1 rounded-full">
                          {formatDateSeparator(msg.timestamp)}
                        </span>
                      </div>
                    )}
                    <div
                      className={`flex ${isUser ? 'justify-end' : 'justify-start'} group mb-1`}
                    >
                      <div className="flex flex-col">
                        <div
                          className={`max-w-[550px] px-4 py-2 rounded-sm text-sm ${
                            isUser
                              ? 'bg-zinc-700 text-white'
                              : isHuman
                              ? 'bg-brand-primary text-white'
                              : isSystem
                              ? 'bg-brand-warning/10 text-zinc-700 border border-brand-warning/30'
                              : 'bg-white text-[#09090B] border border-brand-border'
                          }`}
                        >
                          <div className="flex items-center gap-1 mb-0.5 text-xs opacity-70">
                            {isUser && <User className="w-3 h-3" />}
                            {msg.sender_type === 'agent' && <Bot className="w-3 h-3" />}
                            {isHuman && <Shield className="w-3 h-3" />}
                            {isSystem && <AlertTriangle className="w-3 h-3" />}
                            <span className="font-mono">{msg.sender_name || msg.sender_type}</span>
                          </div>
                          <EmailMessageContext
                            message={msg}
                            channel={selectedConv?.channel}
                            isUserBubble={isUser || isHuman}
                          />
                          <EmailMessageBody
                            message={msg}
                            channel={selectedConv?.channel}
                            isUserBubble={isUser || isHuman}
                          />
                          {selectedConv?.conversation_id && (
                            <MessageAttachments
                              conversationId={selectedConv.conversation_id}
                              message={msg}
                              isUserBubble={isUser}
                            />
                          )}
                          <div className="flex items-center justify-end mt-1">
                            {msg.timestamp && (
                              <span
                                className={`text-[10px] ${isUser || isHuman ? 'text-white/50' : 'text-zinc-400'}`}
                                title={formatDateTime(msg.timestamp)}
                              >
                                {formatTime(msg.timestamp)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Feedback buttons for agent messages */}
                        {msg.sender_type === 'agent' && (
                          <div className="flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-6 w-6"
                              data-testid={`rate-msg-${msg.message_id}`}
                              onClick={() => { setRateMsg(msg); setRateOpen(true); }} title="Calificar">
                              <Star className="w-3 h-3 text-brand-warning" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6"
                              data-testid={`correct-msg-${msg.message_id}`}
                              onClick={() => { setCorrectMsg(msg); setCorrectedText(msg.content); setCorrectOpen(true); }} title="Corregir">
                              <Pencil className="w-3 h-3 text-brand-primary" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6"
                              data-testid={`flag-msg-${msg.message_id}`}
                              onClick={() => handleFlag(msg)} title="Flaggear">
                              <Flag className="w-3 h-3 text-brand-danger" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Supervisor input (only when HITL active) */}
            {isHumanActive && (
              <div className="border-t border-brand-border p-4 bg-white">
                <form onSubmit={handleSendSupervisorMessage} className="flex gap-2">
                  <Input
                    data-testid="supervisor-message-input"
                    value={supervisorMsg}
                    onChange={e => setSupervisorMsg(e.target.value)}
                    placeholder="Responder como supervisor..."
                    className="flex-1 h-10 border-brand-border rounded-sm text-sm"
                    disabled={sending}
                  />
                  <Button
                    type="submit"
                    data-testid="supervisor-send-button"
                    disabled={sending || !supervisorMsg.trim()}
                    className="h-10 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            )}
          </>
        )}
      </div>

      {/* Rating Dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Calificar Respuesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500 bg-brand-surface p-2 rounded-sm max-h-20 overflow-auto">{rateMsg?.content?.slice(0, 200)}</p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(v => (
                <button
                  key={v}
                  data-testid={`rate-star-${v}`}
                  onClick={() => setRateValue(v)}
                  className="p-1"
                >
                  <Star className={`w-6 h-6 ${v <= rateValue ? 'fill-brand-warning text-brand-warning' : 'text-zinc-300'}`} />
                </button>
              ))}
            </div>
            <Input
              data-testid="rate-comment-input"
              value={rateComment}
              onChange={e => setRateComment(e.target.value)}
              placeholder="Comentario (opcional)"
              className="rounded-sm"
            />
            <Button data-testid="rate-submit-button" onClick={handleRate} disabled={rateValue < 1} className="w-full bg-brand-primary text-white rounded-sm">
              Guardar Rating
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Correction Dialog */}
      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Corregir Respuesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em]">Original</Label>
              <p className="text-xs text-zinc-500 bg-brand-surface p-2 rounded-sm max-h-24 overflow-auto">{correctMsg?.content}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em]">Respuesta corregida</Label>
              <textarea
                data-testid="correction-text-input"
                value={correctedText}
                onChange={e => setCorrectedText(e.target.value)}
                className="w-full border border-brand-border rounded-sm p-2 text-sm h-24 focus:ring-2 focus:ring-brand-primary focus:outline-none"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em]">Razon de la correccion</Label>
              <Input
                data-testid="correction-reason-input"
                value={correctionReason}
                onChange={e => setCorrectionReason(e.target.value)}
                placeholder="Por que se corrige?"
                className="rounded-sm"
              />
            </div>
            <Button data-testid="correction-submit-button" onClick={handleCorrection} className="w-full bg-brand-primary text-white rounded-sm">
              Guardar Correccion
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Fusionar conversacion</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">
              Los mensajes de la conversacion origen se uniran en la conversacion actual (
              {getConversationDisplay(selectedConv || {}).primary}).
            </p>
            {selectedMergeSuggestions.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em]">Sugerencias (mismo asunto)</Label>
                <div className="mt-1 space-y-1">
                  {selectedMergeSuggestions.map((s) => (
                    <button
                      key={s.conversation_id}
                      type="button"
                      className={`w-full text-left text-xs border rounded-sm px-2 py-1.5 ${
                        mergeSourceId === s.conversation_id ? 'border-brand-primary bg-brand-surface' : 'border-brand-border'
                      }`}
                      onClick={() => setMergeSourceId(s.conversation_id)}
                    >
                      <span className="block truncate">{s.headline || s.conversation_title || s.conversation_id.slice(0, 8)}</span>
                      {s.updated_at && (
                        <span className="block text-[10px] text-zinc-400 mt-0.5">{formatDateTime(s.updated_at)}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.1em]">Conversacion origen</Label>
              <select
                className="w-full border border-brand-border rounded-sm h-9 px-2 text-xs mt-1"
                value={mergeSourceId}
                onChange={(e) => setMergeSourceId(e.target.value)}
              >
                <option value="">Seleccionar...</option>
                {mergeCandidates.map((c) => (
                  <option key={c.conversation_id} value={c.conversation_id}>
                    {getConversationDisplay(c).primary}
                    {c.updated_at ? ` — ${formatDateTime(c.updated_at)}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em]">Motivo (opcional)</Label>
              <Input
                value={mergeReason}
                onChange={(e) => setMergeReason(e.target.value)}
                placeholder="Ej. mismo caso, cambio de asunto"
                className="rounded-sm text-xs mt-1"
              />
            </div>
            <Button
              className="w-full bg-brand-primary text-white rounded-sm"
              disabled={!mergeSourceId || mergeLoading}
              onClick={handleMergeConversations}
            >
              {mergeLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Continuar...'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />

      <ConfirmActionDialog
        open={purgeOpen}
        onOpenChange={(open) => {
          setPurgeOpen(open);
          if (!open) setPurgePhrase('');
        }}
        title="Vaciar todas las conversaciones"
        description={`Se eliminaran permanentemente todas las conversaciones del tenant. Escriba exactamente: ${PURGE_PHRASE}`}
        confirmText="Vaciar todo"
        confirmDisabled={purgePhrase.trim() !== PURGE_PHRASE || purgeLoading}
        onConfirm={handlePurgeAllConversations}
      >
        <Input
          value={purgePhrase}
          onChange={(e) => setPurgePhrase(e.target.value)}
          placeholder={PURGE_PHRASE}
          className="mt-2 text-xs rounded-sm"
          autoComplete="off"
        />
      </ConfirmActionDialog>
    </div>
  );
}
